/*
 * The workbench shell.
 *
 * It owns the reads that more than one tab needs — the base, its limits, its
 * operation history — so switching tabs is not a re-fetch, and it owns the RUNNING
 * OPERATION, so opening the diff of an ingest that is still streaming does not
 * unmount the stream and lose the deltas.
 *
 * Layout note that cost real time to get right and is invisible until it breaks: the
 * shell's grid row is pinned with `minmax(0, 1fr)` and the shell carries
 * `overflow: hidden`. An `auto` row grows to its content, which stretches the nav
 * rail past the fold and moves the scrollbar onto the document — so the header
 * scrolls away and the rail becomes kilometres tall.
 */
import { useCallback, useMemo } from 'react'
import {
  getLimits,
  getHistory,
  getKnowledgeBase,
  getWikiContent,
  getWikiIndex,
  getLinks,
  listSources,
  WIKI_INDEX_PATH,
  type History,
  type KnowledgeBase,
  type KnowledgeBaseLimits,
  type LinkGraph,
  type SourceList,
  type WikiContent,
  type WikiIndex,
} from '../../api/knowledgeBases.ts'
import { TAB_LABELS, TABS, type TabId } from '../../lib/router.ts'
import { AsyncState } from './components/AsyncState.tsx'
import { QuotaStrip } from './components/QuotaStrip.tsx'
import { ago, num } from './lib/format.ts'
import { leaseFrom, ratesFrom } from './lib/operations.ts'
import { presetName } from './presets.ts'
import { useResource } from './lib/useResource.ts'
import { useOperationRun } from './lib/useOperationRun.ts'
import { ChatTab } from './tabs/ChatTab.tsx'
import { FilesTab } from './tabs/FilesTab.tsx'
import { WikiTab } from './tabs/WikiTab.tsx'
import { GraphTab } from './tabs/GraphTab.tsx'
import { HistoryTab } from './tabs/HistoryTab.tsx'
import { SchemaTab } from './tabs/SchemaTab.tsx'
import { ActivityTab } from './tabs/ActivityTab.tsx'
import { LimitsTab } from './tabs/LimitsTab.tsx'

interface WorkbenchProps {
  kbId: string
  tab: TabId
  onTab: (tab: TabId) => void
  /** Refresh the rail's list after a change that alters a base's counters. */
  onBaseChanged: () => void
}

export function Workbench({ kbId, tab, onTab, onBaseChanged }: WorkbenchProps) {
  const base = useResource<KnowledgeBase>(`${kbId}:base`, () => getKnowledgeBase(kbId))
  const limits = useResource<KnowledgeBaseLimits>(`${kbId}:limits`, () => getLimits(kbId))
  const history = useResource<History>(`${kbId}:history`, () => getHistory(kbId))
  const sources = useResource<SourceList>(`${kbId}:sources`, () => listSources(kbId))
  const wiki = useResource<WikiIndex>(`${kbId}:wiki`, () => getWikiIndex(kbId))
  const links = useResource<LinkGraph>(`${kbId}:links`, () => getLinks(kbId))
  const indexPage = useResource<WikiContent>(`${kbId}:index-page`, () =>
    getWikiContent(kbId, WIKI_INDEX_PATH),
  )

  /** Everything an operation can invalidate, refreshed in one place. */
  const refreshAfterWrite = useCallback(() => {
    base.reload()
    limits.reload()
    history.reload()
    sources.reload()
    wiki.reload()
    links.reload()
    indexPage.reload()
    onBaseChanged()
  }, [base, limits, history, sources, wiki, links, indexPage, onBaseChanged])

  const run = useOperationRun(kbId, refreshAfterWrite)

  const rates = useMemo(() => ratesFrom(limits.data), [limits.data])
  const lease = useMemo(
    () => leaseFrom(limits.data?.wiki_write_lease, history.data, run.active?.runId ?? null),
    [limits.data, history.data, run.active],
  )

  if (base.status !== 'ready' || base.data === null) {
    return (
      <div className="wb">
        <div className="wb__head" style={{ paddingBottom: 16 }}>
          <div className="wb__title">
            <div className="wb__title-main">
              <h1>База знаний</h1>
              <div className="wb__sub mono">{kbId}</div>
            </div>
          </div>
        </div>
        <div className="wb__body">
          <div className="pane">
            <AsyncState
              what="базу"
              status={base.status}
              error={base.error}
              httpStatus={base.httpStatus}
              onRetry={base.reload}
              skeletonRows={4}
            />
          </div>
        </div>
      </div>
    )
  }

  const data = base.data
  const running = run.phase === 'streaming' || run.phase === 'starting'

  return (
    <div className="wb">
      <div className="wb__head">
        <div className="wb__title">
          <div className="wb__title-main">
            <h1>{data.name}</h1>
            <div className="wb__sub">
              {num(sources.data?.total ?? data.raw_files ?? 0)} источников ·{' '}
              {num(wiki.data?.page_count ?? data.wiki_pages ?? 0)} страниц wiki ·{' '}
              {data.last_ingest_at
                ? `последний ingest ${ago(data.last_ingest_at)}`
                : 'ingest ещё не запускали'}
            </div>
          </div>
          <div className="wb__badges">
            {data.preset_key ? (
              <span className="pill pill--info pill--sm">preset: {presetName(data.preset_key)}</span>
            ) : null}
            {limits.data?.plan_id ? (
              <span className="pill pill--neutral pill--sm">план {limits.data.plan_id}</span>
            ) : null}
            {data.archived ? <span className="pill pill--warn pill--sm">в архиве</span> : null}
            {lease.held ? (
              <span
                className="pill pill--warn pill--sm"
                title={
                  lease.mine
                    ? 'Лизу удерживает операция, запущенная из этой вкладки'
                    : 'Лизу удерживает другая операция'
                }
              >
                ◐ wiki заблокирована: {lease.op ? lease.op.op : 'идёт операция'}
              </span>
            ) : running ? (
              <span className="pill pill--warn pill--sm">◐ операция запускается</span>
            ) : null}
          </div>
        </div>

        {limits.data ? (
          <QuotaStrip limits={limits.data} rates={rates} />
        ) : limits.status === 'loading' ? (
          <div className="qstrip">
            {[0, 1, 2, 3, 4].map((slot) => (
              <div className="qs" key={slot}>
                <span className="skeleton" style={{ width: '70%', marginBottom: 6 }} />
                <span className="skeleton" style={{ height: 5 }} />
              </div>
            ))}
          </div>
        ) : (
          <p className="field__hint" style={{ margin: '13px 0 0' }}>
            Лимиты недоступны{limits.httpStatus ? ` · ${limits.httpStatus}` : ''} — счётчики в
            заголовке не показываются, чтобы не выдумывать потолки.
          </p>
        )}

        <div className="wb__tabs" role="tablist">
          {TABS.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              className="wb__tab"
              aria-selected={tab === candidate}
              onClick={() => onTab(candidate)}
            >
              {TAB_LABELS[candidate]}
              <TabCount
                tab={candidate}
                sources={sources.data?.total ?? null}
                pages={wiki.data?.page_count ?? null}
                ops={history.data?.ops?.length ?? null}
                revision={data.schema_revision ?? null}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="wb__body">
        {tab === 'chat' ? (
          <ChatTab
            base={data}
            run={run}
            lease={lease}
            sources={sources}
            wikiIndex={wiki}
            indexPage={indexPage}
            onOpenTab={onTab}
          />
        ) : null}
        {tab === 'files' ? (
          <FilesTab
            kbId={kbId}
            archived={data.archived ?? false}
            sources={sources}
            limits={limits}
            onChanged={onBaseChanged}
          />
        ) : null}
        {tab === 'wiki' ? <WikiTab kbId={kbId} wiki={wiki} links={links} /> : null}
        {tab === 'graph' ? <GraphTab kbId={kbId} links={links} wiki={wiki} /> : null}
        {tab === 'history' ? (
          <HistoryTab
            kbId={kbId}
            history={history}
            gitHead={data.git_head ?? null}
            onChanged={refreshAfterWrite}
          />
        ) : null}
        {tab === 'schema' ? <SchemaTab kbId={kbId} onChanged={refreshAfterWrite} /> : null}
        {tab === 'activity' ? <ActivityTab kbId={kbId} history={history} /> : null}
        {tab === 'limits' ? <LimitsTab limits={limits} rates={rates} /> : null}
      </div>
    </div>
  )
}

/**
 * The count beside a tab label.
 *
 * `null` renders nothing rather than a zero: a tab whose endpoint has not answered
 * yet must not claim the base has none of the thing.
 */
function TabCount({
  tab,
  sources,
  pages,
  ops,
  revision,
}: {
  tab: TabId
  sources: number | null
  pages: number | null
  ops: number | null
  revision: number | null
}) {
  const value =
    tab === 'files'
      ? sources
      : tab === 'wiki'
        ? pages
        : tab === 'history'
          ? ops
          : tab === 'schema'
            ? revision
            : null
  if (value === null) return null
  return <span className="n">{tab === 'schema' ? `r${value}` : num(value)}</span>
}
